const FIT_GUIDE_IMAGE_TOGGLE_SELECTOR = '.fit-guide-image-toggle__button';
const FIT_GUIDE_CATEGORY_MODEL_CLASS = 'fit-guide-drawer__category--show-model';
const FIT_GUIDE_DRAWER_SELECTOR = '[data-fit-guide-drawer]';
const FIT_GUIDE_NAV_BUTTON_SELECTOR = '.fit-guide-navigation__button';
const FIT_GUIDE_NAV_ACTIVE_CLASS = 'fit-guide-navigation__button--active';

const FIT_GUIDE_FAMILY_ITEM_SELECTOR = '.fit-guide-drawer__family-item';
const FIT_GUIDE_FAMILY_ITEM_ACTIVE_CLASS = 'fit-guide-drawer__family-item--active';
const FIT_GUIDE_CURRENT_BADGE_SELECTOR = '.current-badge';
const FIT_GUIDE_SECONDARY_SELECTOR = '[data-fit-guide-secondary]';
const FIT_GUIDE_INNER_SELECTOR = '.fit-guide-drawer__inner';
const FIT_GUIDE_SECONDARY_CLOSE_SELECTOR = '[data-fit-guide-secondary-close]';
const FIT_GUIDE_DIALOG_SELECTOR = '.fit-guide-drawer__dialog';
const FIT_GUIDE_SECONDARY_OPEN_CLASS = 'fit-guide-drawer__dialog--secondary-open';

/**
 * Syncs image visibility class from the toggle button aria-pressed state.
 * @param {Element} toggleButton
 */
function syncCategoryImageState(toggleButton) {
  const category = toggleButton.closest('.fit-guide-drawer__category');
  if (!category) return;

  const isModelView = toggleButton.getAttribute('aria-pressed') === 'true';
  category.classList.toggle(FIT_GUIDE_CATEGORY_MODEL_CLASS, isModelView);
}

function initializeFitGuideImageToggles(root) {
  collectElements(root || document, FIT_GUIDE_IMAGE_TOGGLE_SELECTOR).forEach((toggleButton) =>
    syncCategoryImageState(toggleButton),
  );
}

/**
 * Collects all elements matching selector within root, including root itself
 * when it is an element that matches.
 * @param {Document | Element} root
 * @param {string} selector
 * @returns {Element[]}
 */
function collectElements(root, selector) {
  if (!root || typeof root.querySelectorAll !== 'function') return [];
  const elements = Array.from(root.querySelectorAll(selector));
  if (root instanceof Element && root.matches(selector)) elements.unshift(root);
  return elements;
}

/**
 * Escapes an id for use in a `#id` selector. Category anchors are built from
 * metaobject titles (e.g. "Tops & Tees"), so they can contain spaces or other
 * characters that would break a raw selector.
 * @param {string} id
 * @returns {string}
 */
function escapeId(id) {
  if (window.CSS && typeof CSS.escape === 'function') return CSS.escape(id);
  return id.replace(/(["\\#.:;,?+*~'!^$[\]()=>|/@ ])/g, '\\$1');
}

/**
 * Wires up the fit guide navigation: click-to-scroll + scroll spy.
 * Mirrors the sg-tabs pattern in `snippets/s-size-guide.liquid`.
 * @param {Element} drawer
 */
function initFitGuideNavigation(drawer) {
  if (!(drawer instanceof HTMLElement) || drawer.dataset.fitGuideNavBound === 'true') return;

  const nav = drawer.querySelector('.fit-guide-navigation');
  const content = drawer.querySelector('.fit-guide-drawer__content');
  if (!nav || !content) return;

  const buttons = Array.from(nav.querySelectorAll(FIT_GUIDE_NAV_BUTTON_SELECTOR));
  if (!buttons.length) return;

  drawer.dataset.fitGuideNavBound = 'true';

  /** @type {Element[]} */
  const sections = [];
  /** @type {Map<string, Element>} */
  const buttonByTargetId = new Map();

  buttons.forEach((button) => {
    const targetId = button.getAttribute('data-target');
    if (!targetId) return;
    const section = content.querySelector(`#${escapeId(targetId)}`);
    if (!section) return;
    buttonByTargetId.set(targetId, button);
    sections.push(section);
  });

  // Mirrors the sg-tabs active toggle: clear every button, then set one.
  /** @param {Element | undefined} [button] */
  const setActive = (button) => {
    buttons.forEach((b) => b.classList.remove(FIT_GUIDE_NAV_ACTIVE_CLASS));
    if (button) button.classList.add(FIT_GUIDE_NAV_ACTIVE_CLASS);
  };

  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      const targetId = button.getAttribute('data-target');
      const target = targetId ? content.querySelector(`#${escapeId(targetId)}`) : null;
      if (target) {
        const SCROLL_OFFSET = 20;
        const top =
          target.getBoundingClientRect().top -
          content.getBoundingClientRect().top +
          content.scrollTop -
          SCROLL_OFFSET;
        content.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      }
      setActive(button);
    });
  });

  const firstSection = sections[0];
  if (!firstSection) return;

  setActive(buttonByTargetId.get(firstSection.id));

  const lastSection = sections[sections.length - 1];

  const isScrolledToBottom = () =>
    content.scrollTop + content.clientHeight >= content.scrollHeight - 2;

  const visibility = new Map();
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        visibility.set(entry.target.id, entry.isIntersecting);
      });

      const current = isScrolledToBottom()
        ? lastSection
        : sections.find((section) => visibility.get(section.id));
      if (current) setActive(buttonByTargetId.get(current.id));
    },
    { root: content, rootMargin: '0px 0px -70% 0px', threshold: 0 },
  );

  sections.forEach((section) => observer.observe(section));

  content.addEventListener(
    'scroll',
    () => {
      if (isScrolledToBottom() && lastSection) {
        setActive(buttonByTargetId.get(lastSection.id));
      }
    },
    { passive: true },
  );
}

function initializeFitGuideNavigations(root) {
  collectElements(root || document, FIT_GUIDE_DRAWER_SELECTOR).forEach(initFitGuideNavigation);
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const toggleButton = target?.closest(FIT_GUIDE_IMAGE_TOGGLE_SELECTOR);

  if (!toggleButton) return;

  const isPressed = toggleButton.getAttribute('aria-pressed') === 'true';
  toggleButton.setAttribute('aria-pressed', isPressed ? 'false' : 'true');
  syncCategoryImageState(toggleButton);
});

/**
 * @param {Element} item - The `.fit-guide-drawer__family-item` that was activated.
 */
function openSecondaryPanel(item) {
  const drawer = item.closest(FIT_GUIDE_DRAWER_SELECTOR);
  if (!drawer) return;

  const secondary = drawer.querySelector(FIT_GUIDE_SECONDARY_SELECTOR);
  const dialog = drawer.querySelector(FIT_GUIDE_DIALOG_SELECTOR);
  if (!secondary || !dialog) return;

  const familyName =
    (item instanceof HTMLElement && item.dataset.familyName) ||
    item.querySelector('.fit-guide-drawer__family-name')?.textContent?.trim() ||
    '';

  const titleName = secondary.querySelector('[data-fit-guide-secondary-title-name]');
  if (titleName) titleName.textContent = familyName;

  const body = secondary.querySelector('[data-fit-guide-secondary-body]');
  const template = item.querySelector('[data-fit-guide-family-content]');
  if (body && template instanceof HTMLTemplateElement) {
    body.replaceChildren(template.content.cloneNode(true));
  }

  // Mark the clicked item as active, clearing any previously active sibling.
  drawer
    .querySelectorAll(`.${FIT_GUIDE_FAMILY_ITEM_ACTIVE_CLASS}`)
    .forEach((activeItem) => activeItem.classList.remove(FIT_GUIDE_FAMILY_ITEM_ACTIVE_CLASS));
  item.classList.add(FIT_GUIDE_FAMILY_ITEM_ACTIVE_CLASS);

  secondary.setAttribute('data-visible', '');
  dialog.classList.add(FIT_GUIDE_SECONDARY_OPEN_CLASS);
}

/**
 * Closes the secondary panel for a given drawer (no-op if already closed).
 * @param {Element} drawer - The `#fit-guide-drawer` dialog-component.
 */
function closeSecondaryPanel(drawer) {
  const secondary = drawer.querySelector(FIT_GUIDE_SECONDARY_SELECTOR);
  const dialog = drawer.querySelector(FIT_GUIDE_DIALOG_SELECTOR);
  secondary?.removeAttribute('data-visible');
  dialog?.classList.remove(FIT_GUIDE_SECONDARY_OPEN_CLASS);
  drawer
    .querySelectorAll(`.${FIT_GUIDE_FAMILY_ITEM_ACTIVE_CLASS}`)
    .forEach((activeItem) => activeItem.classList.remove(FIT_GUIDE_FAMILY_ITEM_ACTIVE_CLASS));
}

/**
 * Resolves the clicked family item from a click event.
 *
 * @param {Event} event
 * @returns {Element | null}
 */
function resolveFamilyItem(event) {
  const target = event.target instanceof Element ? event.target : null;
  const fromTarget = target?.closest(FIT_GUIDE_FAMILY_ITEM_SELECTOR);
  if (fromTarget) return fromTarget;

  const { clientX, clientY } = /** @type {MouseEvent} */ (event);
  if (typeof clientX !== 'number' || (!clientX && !clientY)) return null;

  return (
    document.elementFromPoint(clientX, clientY)?.closest(FIT_GUIDE_FAMILY_ITEM_SELECTOR) ?? null
  );
}

document.addEventListener(
  'mousedown',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest(FIT_GUIDE_FAMILY_ITEM_SELECTOR)) event.stopPropagation();
  },
  true,
);

// Open on family-item click; close on back-button / overlay click.
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const closer = target.closest(FIT_GUIDE_SECONDARY_CLOSE_SELECTOR);
  if (closer) {
    const drawer = closer.closest(FIT_GUIDE_DRAWER_SELECTOR);
    if (drawer) closeSecondaryPanel(drawer);
    return;
  }

  const item = resolveFamilyItem(event);
  if (item) {
    if (item.classList.contains(FIT_GUIDE_FAMILY_ITEM_ACTIVE_CLASS)) {
      const drawer = item.closest(FIT_GUIDE_DRAWER_SELECTOR);
      if (drawer) closeSecondaryPanel(drawer);
    } else {
      openSecondaryPanel(item);
    }
    return;
  }

  const inner = target.closest(FIT_GUIDE_INNER_SELECTOR);
  if (inner) {
    const drawer = inner.closest(FIT_GUIDE_DRAWER_SELECTOR);
    if (drawer) closeSecondaryPanel(drawer);
  }
});

// Keyboard activation for the role="button" family items.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  const target = event.target instanceof Element ? event.target : null;
  const item = target?.closest(FIT_GUIDE_FAMILY_ITEM_SELECTOR);
  if (!item) return;
  event.preventDefault();
  openSecondaryPanel(item);
});

// Let Escape dismiss the secondary panel first (before the dialog component's
// own Escape handler closes the whole drawer). Capture phase to run earliest.
document.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Escape') return;
    const secondary = document.querySelector(
      `${FIT_GUIDE_DRAWER_SELECTOR} ${FIT_GUIDE_SECONDARY_SELECTOR}[data-visible]`,
    );
    if (!secondary) return;
    event.preventDefault();
    event.stopPropagation();
    const drawer = secondary.closest(FIT_GUIDE_DRAWER_SELECTOR);
    if (drawer) closeSecondaryPanel(drawer);
  },
  true,
);

const FIT_GUIDE_CATEGORY_SELECTOR = '.fit-guide-drawer__category';
const FIT_GUIDE_CATEGORY_TITLE_SELECTOR = '.fit-guide-drawer__category-title';
const FIT_GUIDE_CONTENT_SELECTOR = '.fit-guide-drawer__content';
const FIT_GUIDE_LAYOUT_MAX_FRAMES = 45;

/**.
 * @param {HTMLElement} content
 * @param {() => void} callback
 */
function whenContentStable(content, callback) {
  let lastHeight = -1;
  let frames = 0;

  const check = () => {
    const height = content.scrollHeight;
    if (height !== lastHeight) {
      lastHeight = height;
      callback();
    }
    frames += 1;

    if (frames >= FIT_GUIDE_LAYOUT_MAX_FRAMES) return;
    requestAnimationFrame(check);
  };

  requestAnimationFrame(check);
}

/**
 * @param {Element} drawer - The `#fit-guide-drawer` dialog-component.
 */
function activateCurrentFamilyNav(drawer) {
  const currentItem = drawer
    .querySelector(FIT_GUIDE_CURRENT_BADGE_SELECTOR)
    ?.closest(FIT_GUIDE_FAMILY_ITEM_SELECTOR);
  if (!currentItem) return;

  const targetId = currentItem
    .closest(FIT_GUIDE_CATEGORY_SELECTOR)
    ?.querySelector(FIT_GUIDE_CATEGORY_TITLE_SELECTOR)?.id;
  if (!targetId) return;

  const navButton = Array.from(drawer.querySelectorAll(FIT_GUIDE_NAV_BUTTON_SELECTOR)).find(
    (button) => button.getAttribute('data-target') === targetId,
  );

  if (!(navButton instanceof HTMLElement)) return;

  const content = drawer.querySelector(FIT_GUIDE_CONTENT_SELECTOR);
  if (content instanceof HTMLElement) {
    whenContentStable(content, () => navButton.click());
  } else {
    navButton.click();
  }
}

/**
 * @param {Element} drawer - The `#fit-guide-drawer` dialog-component.
 */
function initFitGuideSecondaryLifecycle(drawer) {
  if (!(drawer instanceof HTMLElement) || drawer.dataset.fitGuideSecondaryBound === 'true') return;
  drawer.dataset.fitGuideSecondaryBound = 'true';
  drawer.addEventListener('dialog:open', () => activateCurrentFamilyNav(drawer));
  drawer.addEventListener('dialog:close', () => closeSecondaryPanel(drawer));
}

function initializeFitGuideSecondaryResets(root) {
  collectElements(root || document, FIT_GUIDE_DRAWER_SELECTOR).forEach(
    initFitGuideSecondaryLifecycle,
  );
}

const FIT_GUIDE_SECONDARY_SHEET_SELECTOR = '[data-fit-guide-secondary-sheet]';
const FIT_GUIDE_SWIPE_THRESHOLD = 60;
const fitGuideMobileQuery = matchMedia('(max-width: 1079px)');

/**
 * @param {Element} drawer - The `#fit-guide-drawer` dialog-component.
 */
function initFitGuideSecondarySwipe(drawer) {
  const sheet = drawer.querySelector(FIT_GUIDE_SECONDARY_SHEET_SELECTOR);
  const secondary = drawer.querySelector(FIT_GUIDE_SECONDARY_SELECTOR);
  if (!(sheet instanceof HTMLElement) || !secondary || sheet.dataset.fitGuideSwipeBound === 'true')
    return;
  sheet.dataset.fitGuideSwipeBound = 'true';

  let startY = 0;
  let currentY = 0;
  let active = false;

  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isEngaged = () => fitGuideMobileQuery.matches && secondary.hasAttribute('data-visible');

  sheet.addEventListener(
    'touchstart',
    (event) => {
      if (!isEngaged()) return;
      // Take over touch handling so the dialog's swipe-to-close is suppressed.
      event.stopPropagation();

      const touch = event.touches[0];
      if (!touch) return;

      const body = sheet.querySelector('.fit-guide-drawer__secondary-body');
      if (body instanceof HTMLElement && body.scrollTop > 0) {
        active = false;
        return;
      }

      active = true;
      startY = touch.clientY;
      currentY = startY;
      sheet.style.transition = 'none';
    },
    { passive: true },
  );

  sheet.addEventListener(
    'touchmove',
    (event) => {
      if (!isEngaged()) return;
      event.stopPropagation();
      if (!active) return;

      const touch = event.touches[0];
      if (!touch) return;

      const deltaY = touch.clientY - startY;
      if (deltaY <= 0) return;

      event.preventDefault();
      currentY = touch.clientY;
      if (!reducedMotion()) sheet.style.transform = `translateY(${deltaY}px)`;
    },
    { passive: false },
  );

  sheet.addEventListener(
    'touchend',
    () => {
      if (!active) return;
      active = false;

      const deltaY = currentY - startY;

      if (deltaY >= FIT_GUIDE_SWIPE_THRESHOLD) {
        sheet.style.transition = '';
        sheet.style.transform = '';
        closeSecondaryPanel(drawer);
        return;
      }

      sheet.style.transition = 'transform 0.2s ease';
      sheet.style.transform = '';
      sheet.addEventListener(
        'transitionend',
        () => {
          sheet.style.transition = '';
        },
        { once: true },
      );
    },
    { passive: true },
  );
}

function initializeFitGuideSecondarySwipes(root) {
  collectElements(root || document, FIT_GUIDE_DRAWER_SELECTOR).forEach(initFitGuideSecondarySwipe);
}

const FIT_GUIDE_FAMILY_SLIDESHOW_SELECTOR = '.fit-guide-drawer__family-slideshow';
const FIT_GUIDE_ARROW_PREV_SELECTOR = '.slideshow-control--previous';
const FIT_GUIDE_ARROW_NEXT_SELECTOR = '.slideshow-control--next';

/**
 * Makes the family carousel arrows advance one card at a time.
 * @param {Element} drawer - The `#fit-guide-drawer` dialog-component.
 */
function initFitGuideSlideshowArrows(drawer) {
  drawer.querySelectorAll(FIT_GUIDE_FAMILY_SLIDESHOW_SELECTOR).forEach((slideshow) => {
    if (!(slideshow instanceof HTMLElement) || slideshow.dataset.fitGuideArrowsBound === 'true') {
      return;
    }

    const previous = slideshow.querySelector(FIT_GUIDE_ARROW_PREV_SELECTOR);
    const next = slideshow.querySelector(FIT_GUIDE_ARROW_NEXT_SELECTOR);
    if (!previous && !next) return;

    slideshow.dataset.fitGuideArrowsBound = 'true';

    previous?.removeAttribute('on:click');
    next?.removeAttribute('on:click');

    /**
     * @param {number} delta
     * @param {Event} event
     */
    const step = (delta, event) => {
      const component = /** @type {any} */ (slideshow);
      if (typeof component.select !== 'function') return;
      const current = Number(component.current) || 0;
      component.select(current + delta, event);
    };

    previous?.addEventListener('click', (event) => step(-1, event));
    next?.addEventListener('click', (event) => step(1, event));
  });
}

function initializeFitGuideSlideshowArrows(root) {
  collectElements(root || document, FIT_GUIDE_DRAWER_SELECTOR).forEach(initFitGuideSlideshowArrows);
}

/**
 * @param {Element} drawer - The `#fit-guide-drawer` dialog-component.
 */
function initFitGuideCarouselSwipeGuard(drawer) {
  drawer.querySelectorAll(FIT_GUIDE_FAMILY_SLIDESHOW_SELECTOR).forEach((slideshow) => {
    if (
      !(slideshow instanceof HTMLElement) ||
      slideshow.dataset.fitGuideSwipeGuardBound === 'true'
    ) {
      return;
    }
    slideshow.dataset.fitGuideSwipeGuardBound = 'true';

    /** @param {Event} event */
    const stopPropagation = (event) => event.stopPropagation();
    slideshow.addEventListener('touchstart', stopPropagation, { passive: true });
    slideshow.addEventListener('touchmove', stopPropagation, { passive: true });
    slideshow.addEventListener('touchend', stopPropagation, { passive: true });
  });
}

function initializeFitGuideCarouselSwipeGuards(root) {
  collectElements(root || document, FIT_GUIDE_DRAWER_SELECTOR).forEach(
    initFitGuideCarouselSwipeGuard,
  );
}

const FIT_GUIDE_NAVIGATION_SELECTOR = '.fit-guide-navigation';

/**
 * @param {Element} drawer - The `#fit-guide-drawer` dialog-component.
 */
function initFitGuideNavigationSwipeGuard(drawer) {
  const nav = drawer.querySelector(FIT_GUIDE_NAVIGATION_SELECTOR);
  if (!(nav instanceof HTMLElement) || nav.dataset.fitGuideSwipeGuardBound === 'true') return;
  nav.dataset.fitGuideSwipeGuardBound = 'true';

  /** @param {Event} event */
  const stopPropagation = (event) => event.stopPropagation();
  nav.addEventListener('touchstart', stopPropagation, { passive: true });
  nav.addEventListener('touchmove', stopPropagation, { passive: true });
  nav.addEventListener('touchend', stopPropagation, { passive: true });
}

function initializeFitGuideNavigationSwipeGuards(root) {
  collectElements(root || document, FIT_GUIDE_DRAWER_SELECTOR).forEach(
    initFitGuideNavigationSwipeGuard,
  );
}

const FIT_GUIDE_URL_PARAM = 'fit-guide';
const FIT_GUIDE_URL_PARAM_OPEN_VALUE = 'open';
const DIALOG_COMPONENT_TAG = 'dialog-component';

let fitGuideAutoOpenHandled = false;

/**
 * @returns {boolean} Whether the current URL asks for the drawer to be opened.
 */
function isFitGuideUrlOpenRequested() {
  const value = new URLSearchParams(window.location.search).get(FIT_GUIDE_URL_PARAM);
  return value?.trim().toLowerCase() === FIT_GUIDE_URL_PARAM_OPEN_VALUE;
}

/**
 * Drops `fit-guide` from the address bar, keeping every other param.
 *
 * Constructor rebuilds facet/sort/pagination URLs from `window.location`, so an
 * unrecognised param would otherwise ride along through the whole browsing
 * session and re-open the drawer on any of those URLs when shared or reloaded.
 */
function removeFitGuideUrlParam() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(FIT_GUIDE_URL_PARAM)) return;
  url.searchParams.delete(FIT_GUIDE_URL_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

/**
 * Opens the drawer on landing when the URL requests it — e.g. a campaign link to
 * `/collections/jeans?fit-guide=open`.
 *
 * Opens whichever single `#fit-guide-drawer` is on the page (collection pages render it from
 * `featured-filters`, product pages from the variant picker), so it needs no knowledge of which
 * guide that is. Runs once per page, so a drawer mounted later by Constructor hotspots still
 * gets a chance to honour the param.
 *
 * @param {Document | Element} root
 */
function autoOpenFitGuideFromUrl(root) {
  if (fitGuideAutoOpenHandled || !isFitGuideUrlOpenRequested()) return;

  const drawer = collectElements(root, FIT_GUIDE_DRAWER_SELECTOR)[0];
  if (!drawer) return;

  fitGuideAutoOpenHandled = true;
  removeFitGuideUrlParam();

  // `showDialog` only exists once <dialog-component> is defined; `dialog.js` is
  // a low-priority module, so it may still be in flight at this point.
  customElements.whenDefined(DIALOG_COMPONENT_TAG).then(() => {
    if (!drawer.isConnected || !('showDialog' in drawer)) return;
    /** @type {Element & { showDialog: () => void }} */ (drawer).showDialog();
  });
}

/**
 * Initializes fit guide drawer interactions for the full document or
 * a newly-inserted subtree.
 * @param {Document | Element} [root]
 */
function initializeFitGuideDrawer(root = document) {
  initializeFitGuideImageToggles(root);
  initializeFitGuideNavigations(root);
  initializeFitGuideSecondaryResets(root);
  initializeFitGuideSecondarySwipes(root);
  initializeFitGuideSlideshowArrows(root);
  initializeFitGuideCarouselSwipeGuards(root);
  initializeFitGuideNavigationSwipeGuards(root);
  autoOpenFitGuideFromUrl(root);
}

window.FitGuideDrawer = window.FitGuideDrawer || {};
window.FitGuideDrawer.initialize = initializeFitGuideDrawer;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeFitGuideDrawer, { once: true });
} else {
  initializeFitGuideDrawer();
}
